<!-- src/components/pettyCash/PettyCashForm.vue -->
<template>
  <div class="card-panel petty-cash-form">
    <div class="row petty-cash-form-row">
      <!-- DESCRIPTION -->
      <div class="col s12 m4 input-field">
        <input
          id="pc_desc"
          v-model.trim="state.description"
          type="text"
          maxlength="120"
          :disabled="loading"
        />

        <label class="active" for="pc_desc"> Item description </label>
      </div>

      <!-- CATEGORY -->
      <div class="col s12 m3 input-field">
        <select ref="categoryEl" v-model="state.category" :disabled="loading">
          <option value="" disabled>Select category</option>

          <option v-for="c in categories" :key="c" :value="c">
            {{ c }}
          </option>
        </select>

        <label class="active"> Expense category </label>
      </div>

      <!-- AMOUNT -->
      <div class="col s12 m2 input-field">
        <input
          id="pc_amount"
          v-model.number="state.amount"
          type="number"
          step="0.01"
          min="0"
          :disabled="loading"
        />

        <label class="active" for="pc_amount"> Amount (£) </label>
      </div>

      <!-- EXPENSE DATE -->
      <div class="col s12 m3 input-field">
        <input
          id="pc_date"
          v-model="state.expenseDateISO"
          type="date"
          :disabled="loading"
        />

        <label class="active" for="pc_date"> Date of expense </label>
      </div>
    </div>

    <!-- =====================================================
         CASHLESS RECEIPT
         ===================================================== -->
    <div v-if="cashless" class="receipt-panel">
      <div class="receipt-title">
        <i class="material-icons"> receipt_long </i>

        <div>
          <strong> Receipt required </strong>

          <div class="receipt-help">
            Upload a JPG, PNG or PDF receipt. Maximum file size 6 MB.
          </div>
        </div>
      </div>

      <input
        ref="receiptInput"
        class="receipt-file-input"
        type="file"
        accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
        :disabled="loading"
        @change="onReceiptChange"
      />

      <div class="core-actions receipt-actions">
        <button
          type="button"
          class="btn blue-grey darken-2 waves-effect waves-light"
          :disabled="loading"
          @click="chooseReceipt"
        >
          <i class="material-icons left"> attach_file </i>

          {{ state.receiptFile ? "Change receipt" : "Upload receipt" }}
        </button>

        <button
          v-if="state.receiptFile"
          type="button"
          class="btn-flat red-text text-darken-2"
          :disabled="loading"
          @click="clearReceipt"
        >
          Remove
        </button>
      </div>

      <div v-if="state.receiptFile" class="selected-receipt">
        <i class="material-icons tiny"> check_circle </i>

        <span>
          {{ state.receiptFile.name }}
        </span>

        <span class="grey-text"> ({{ formatFileSize(state.receiptFile.size) }}) </span>
      </div>

      <div v-if="fileError" class="red-text text-darken-2 receipt-error">
        {{ fileError }}
      </div>
    </div>

    <!-- =====================================================
         SAVE
         ===================================================== -->
    <div class="row petty-cash-form-actions-row">
      <div class="col s12">
        <button
          type="button"
          class="btn green darken-1 petty-cash-save-btn"
          :disabled="loading || !isValid"
          @click="onSave"
        >
          <i v-if="cashless" class="material-icons left"> save </i>

          Save
        </button>

        <span
          v-if="cashless && !state.receiptFile"
          class="grey-text text-darken-1 receipt-required-note"
        >
          A receipt must be uploaded before this expense can be saved.
        </span>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from "vue";

/* =========================================================
   PROPS
   ========================================================= */

const props = defineProps({
  loading: {
    type: Boolean,
    default: false,
  },

  categories: {
    type: Array,
    required: true,
  },

  cashless: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["save"]);

/* =========================================================
   RECEIPT CONFIG
   ========================================================= */

const MAX_RECEIPT_BYTES = 6 * 1024 * 1024;

const ALLOWED_RECEIPT_TYPES = new Set(["image/jpeg", "image/png", "application/pdf"]);

/* =========================================================
   DATE
   ========================================================= */

function todayISO() {
  const d = new Date();

  const y = d.getFullYear();

  const m = String(d.getMonth() + 1).padStart(2, "0");

  const day = String(d.getDate()).padStart(2, "0");

  return `${y}-${m}-${day}`;
}

function isoToDate(iso) {
  const [y, m, d] = String(iso || "")
    .split("-")
    .map(Number);

  if (!y || !m || !d) {
    return new Date();
  }

  return new Date(y, m - 1, d);
}

/* =========================================================
   STATE
   ========================================================= */

const state = reactive({
  description: "",
  category: "",
  amount: 0,
  expenseDateISO: todayISO(),
  receiptFile: null,
});

const fileError = ref("");

const receiptInput = ref(null);

/* =========================================================
   VALIDATION
   ========================================================= */

const isValid = computed(() => {
  const baseValid =
    String(state.description || "").trim().length > 0 &&
    String(state.category || "").trim().length > 0 &&
    Number.isFinite(Number(state.amount)) &&
    Number(state.amount) > 0 &&
    /^\d{4}-\d{2}-\d{2}$/.test(String(state.expenseDateISO || ""));

  if (!baseValid) {
    return false;
  }

  /*
   * Normal cash venues do not require a receipt.
   */
  if (!props.cashless) {
    return true;
  }

  /*
   * Cashless venues require a receipt.
   */
  return Boolean(state.receiptFile);
});

/* =========================================================
   RECEIPT
   ========================================================= */

function chooseReceipt() {
  if (props.loading) {
    return;
  }

  receiptInput.value?.click();
}

function clearReceipt() {
  state.receiptFile = null;

  if (receiptInput.value) {
    receiptInput.value.value = "";
  }
}

function onReceiptChange(event) {
  fileError.value = "";

  const file = event?.target?.files?.[0] || null;

  if (!file) {
    state.receiptFile = null;

    return;
  }

  if (!ALLOWED_RECEIPT_TYPES.has(file.type)) {
    clearReceipt();

    fileError.value = "Receipt must be a JPG, PNG or PDF.";

    return;
  }

  if (file.size > MAX_RECEIPT_BYTES) {
    clearReceipt();

    fileError.value = "Receipt is too large. Maximum file size is 6 MB.";

    return;
  }

  state.receiptFile = file;
}

function formatFileSize(bytes) {
  const value = Number(bytes || 0);

  if (value < 1024) {
    return `${value} bytes`;
  }

  if (value < 1024 * 1024) {
    return `${(value / 1024).toFixed(1)} KB`;
  }

  return `${(value / 1024 / 1024).toFixed(1)} MB`;
}

/* =========================================================
   MATERIALIZE SELECT
   ========================================================= */

const categoryEl = ref(null);

let formSelectInstance = null;

function destroyFormSelect() {
  try {
    if (formSelectInstance && formSelectInstance.destroy) {
      formSelectInstance.destroy();
    }
  } catch (_) {
    // Ignore teardown issue.
  }

  formSelectInstance = null;
}

async function initFormSelect() {
  await nextTick();

  const M = window.M;

  if (!M || !categoryEl.value) {
    return;
  }

  destroyFormSelect();

  formSelectInstance = M.FormSelect.init(categoryEl.value);

  if (M.updateTextFields) {
    M.updateTextFields();
  }
}

onMounted(async () => {
  await initFormSelect();
});

watch(
  () => props.categories,
  async () => {
    await initFormSelect();
  }
);

watch(
  () => props.loading,
  async () => {
    await initFormSelect();
  }
);

watch(
  () => props.cashless,
  async (cashless) => {
    if (!cashless) {
      clearReceipt();
    }

    await initFormSelect();
  }
);

onBeforeUnmount(() => {
  destroyFormSelect();
});

/* =========================================================
   SAVE
   ========================================================= */

function onSave() {
  if (!isValid.value || props.loading) {
    return;
  }

  emit("save", {
    description: state.description,

    category: state.category,

    amount: Number(state.amount),

    expenseDate: isoToDate(state.expenseDateISO),

    receiptFile: state.receiptFile,
  });

  state.description = "";

  state.category = "";

  state.amount = 0;

  state.expenseDateISO = todayISO();

  clearReceipt();

  initFormSelect();
}
</script>

<style scoped>
.petty-cash-form {
  margin-top: 12px;
}

.petty-cash-form-row {
  margin-bottom: 0;
}

.petty-cash-form-actions-row {
  margin-bottom: 0;
  margin-top: 12px;
}

.receipt-panel {
  margin-top: 14px;
  padding: 16px;

  border: 1px solid #cfd8dc;
  border-radius: 12px;

  background: #fafcfd;
}

.receipt-title {
  display: flex;
  align-items: flex-start;

  gap: 10px;

  color: #37474f;
}

.receipt-help {
  margin-top: 3px;

  color: #78909c;

  font-size: 12.5px;
}

.receipt-file-input {
  display: none;
}

.receipt-actions {
  margin-top: 14px;
}

.selected-receipt {
  display: flex;
  align-items: center;

  gap: 6px;

  margin-top: 12px;

  color: #2e7d32;

  font-size: 13px;
}

.receipt-error {
  margin-top: 10px;

  font-size: 13px;
}

.receipt-required-note {
  display: inline-block;

  margin-left: 12px;

  font-size: 12.5px;
}

@media (max-width: 600px) {
  .petty-cash-form {
    padding: 14px;
  }

  .petty-cash-form .input-field {
    margin-bottom: 6px;
  }

  .receipt-required-note {
    display: block;

    margin-left: 0;
    margin-top: 8px;
  }
}
</style>
